import React, { useState } from 'react';
import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import {
  OrderEditingSettings,
  OrderEditingWorkspace,
} from 'types/order-editing';
import { APPEARANCE_DEFAULTS } from './appearance';
import { normalizeOrderEditingSettings } from './expandedSettings';
import { actionLabel } from './expandedSettings';

export const inputClass =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:ring-blue-600 disabled:bg-gray-100';
export const cardClass = 'rounded-lg border border-gray-200 bg-white p-5';
export const displayDate = (value: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};
export const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    cents / 100,
  );
export const orderEditingFee = (priceCents?: number) =>
  money(
    typeof priceCents === 'number' &&
      Number.isSafeInteger(priceCents) &&
      priceCents >= 0
      ? priceCents
      : 10,
  );

export function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    ready: 'Ready',
    action_required: 'Action required',
    unknown: 'Unknown',
    pending: 'Pending',
    succeeded: 'Completed',
    fee_recorded: 'Fee recorded',
    failed: 'Failed',
    needs_review: 'Needs review',
    retryable: 'Retry pending',
    payment_required: 'Payment required',
    refund_required: 'Refund required',
    settled: 'Settled',
    review_required: 'Review required',
    held: 'Held',
    released: 'Released',
    release_pending: 'Release pending',
    reserved: 'Reserved',
    placing: 'Placing hold',
    releasing: 'Releasing',
    uncertain: 'Needs review',
    sending: 'Sending',
    cancelled: 'Cancelled',
  };
  const color =
    value === 'ready' || value === 'succeeded' || value === 'fee_recorded'
      ? 'bg-green-50 text-green-800'
      : value === 'unknown'
      ? 'bg-gray-100 text-gray-700'
      : 'bg-amber-50 text-amber-900';
  return (
    <span
      className={`inline-flex rounded px-2 py-1 text-xs font-medium ${color}`}
    >
      {labels[value] || 'Unknown'}
    </span>
  );
}

export function SettingToggle({
  label,
  description,
  value,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-5 border-b border-gray-100 py-3 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {description && (
          <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
        )}
      </div>
      <Toggle
        isEnabled={value}
        onChange={onChange}
        ariaLabel={label}
        disabled={disabled}
        isSmall
      />
    </div>
  );
}

export function Address({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object')
    return <span className="text-gray-500">Not recorded</span>;
  const address = value as Record<string, unknown>;
  const name = [address.firstName, address.lastName]
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(' ');
  const parts = [
    'company',
    'address1',
    'address2',
    'city',
    'provinceCode',
    'zip',
    'countryCode',
    'countryCodeV2',
  ];
  const lines = [name, ...parts.map((key) => address[key])].filter(
    (item): item is string => typeof item === 'string' && !!item.trim(),
  );
  return (
    <div>
      {Array.from(new Set(lines)).map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  );
}

type InstallationProps = {
  workspace: OrderEditingWorkspace;
  refreshing: boolean;
  onRefresh: () => void;
};

export function InstallationNotice(props: InstallationProps) {
  const [expanded, setExpanded] = useState(false);
  const { setup, settings } = props.workspace;
  const required = ['app_identity', 'order_scopes', 'checkout_profile'];
  if (settings.editShipping && settings.verifyAddresses)
    required.push('address_verification');
  if (
    settings.guestAccess ||
    (settings.expanded?.contactEmail &&
      settings.expanded.contactEmail !== 'off')
  )
    required.push('guest_email_access', 'guest_access');
  const checksReady = required.every((id) =>
    setup.checks?.some((check) => check.id === id && check.status === 'ready'),
  );
  const actionsReady = Object.entries(settings.expanded || {}).every(
    ([key, mode]) =>
      key === 'shippingUpgrade' ||
      mode === 'off' ||
      setup.capabilities?.[key as keyof NonNullable<typeof setup.capabilities>]
        ?.status === 'ready',
  );
  const pagesObserved = (['thankYou', 'orderStatus'] as const).every(
    (key) => {
      const surface = setup.surfaces?.[key];
      return (
        surface?.status === 'observed' &&
        !!surface.lastSeenAt &&
        Number.isFinite(Date.parse(surface.lastSeenAt))
      );
    },
  );
  const needsAttention =
    !checksReady ||
    !actionsReady ||
    !pagesObserved ||
    setup.billing.status !== 'ready';
  return (
    <div className="space-y-3">
      {needsAttention ? (
        <section
          aria-label="Installation needs attention"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-amber-950">
                Finish Order Editing setup
              </h2>
              <p className="mt-1 text-sm text-amber-900">
                Check app access and add the block to your Thank You and Order
                Status pages in Shopify.
              </p>
            </div>
            {setup.editorUrl && (
              <a
                href={setup.editorUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-700 underline"
              >
                Open Shopify checkout editor
              </a>
            )}
          </div>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            className="mt-2 text-sm font-medium text-amber-950 underline"
          >
            {expanded
              ? 'Hide setup checks'
              : 'View setup checks and instructions'}
          </button>
        </section>
      ) : (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className="text-xs font-medium text-gray-600 underline"
        >
          {expanded
            ? 'Hide setup checks'
            : 'Setup checks passed · both pages observed'}
        </button>
      )}
      {expanded && <Setup {...props} />}
    </div>
  );
}

export function Setup({ workspace, refreshing, onRefresh }: InstallationProps) {
  const { setup } = workspace;
  return (
    <section
      id="order-edit-setup"
      className={cardClass}
      aria-labelledby="setup-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="setup-heading" className="text-base font-semibold">
          Installation & readiness
        </h2>
        <Button variant="outlined" disabled={refreshing} onClick={onRefresh}>
          Refresh checks
        </Button>
      </div>
      <p className="mt-2 text-sm text-gray-600">
        {setup.appType === 'custom' ? 'Custom Shopify app' : 'Shopify app'} ·
        Checks reflect the latest available server information.
      </p>
      <div className="mt-4 divide-y divide-gray-100">
        {setup.checks?.map((check) => (
          <div
            key={check.id}
            className="flex items-start justify-between gap-4 py-3"
          >
            <div>
              <p className="text-sm font-medium">{check.label}</p>
              <p className="mt-1 text-xs text-gray-600">{check.detail}</p>
            </div>
            <Status value={check.status} />
          </div>
        ))}
        {!setup.checks?.length && (
          <p className="py-3 text-sm text-gray-500">
            Setup checks are not available yet.
          </p>
        )}
        <div className="flex items-start justify-between gap-4 py-3">
          <div>
            <p className="text-sm font-medium">Existing usage billing</p>
            <p className="mt-1 text-xs text-gray-600">{setup.billing.detail}</p>
          </div>
          <Status value={setup.billing.status} />
        </div>
        {Object.entries(workspace.settings.expanded || {})
          .filter(([key, mode]) => key !== 'shippingUpgrade' && mode !== 'off')
          .map(([key]) => {
            const capability =
              setup.capabilities?.[
                key as keyof NonNullable<typeof setup.capabilities>
              ];
            return (
              <div
                key={key}
                className="flex items-start justify-between gap-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium">{actionLabel(key)}</p>
                  <p className="mt-1 text-xs text-gray-600">
                    {capability?.detail || 'Readiness has not been reported.'}
                  </p>
                </div>
                <Status value={capability?.status || 'unknown'} />
              </div>
            );
          })}
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {(
          [
            ['thankYou', 'Thank You page'],
            ['orderStatus', 'Order Status page'],
          ] as const
        ).map(([key, title]) => {
          const surface = setup.surfaces?.[key];
          return (
            <section
              key={key}
              aria-label={title}
              className="rounded-md border border-gray-200 p-4"
            >
              <h3 className="text-sm font-semibold">{title}</h3>
              <p className="mt-2 text-sm">
                {surface?.lastSeenAt
                  ? `Observed ${displayDate(surface.lastSeenAt)}`
                  : 'Not observed — placement is unknown'}
              </p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                A recent authenticated widget visit is an observation, not proof
                that this block is published on every checkout.
              </p>
            </section>
          );
        })}
      </div>
      {setup.editorUrl ? (
        <a
          className="mt-4 inline-block text-sm font-medium text-blue-700 underline"
          href={setup.editorUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Shopify checkout editor
        </a>
      ) : (
        <p className="mt-4 text-sm text-gray-600">
          Open Settings → Checkout in your Shopify admin to edit the checkout
          configuration.
        </p>
      )}
      <details className="mt-4 rounded-md bg-gray-50 p-4">
        <summary className="cursor-pointer text-sm font-medium">
          Installation & test checklist
        </summary>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-600">
          <li>
            Resolve any app permission, customer data, guest verification or
            billing checks above.
          </li>
          <li>
            In the checkout editor, add the ShipAid Order Edit block to the
            Thank You page and save.
          </li>
          <li>Add the block separately to the Order Status page and save.</li>
          <li>
            Save your editing rules. Enable only the actions your team has
            configured and tested.
          </li>
          <li>
            Use a test order inside the editing window. Check both pages,
            address validation, and the signed-in or verified guest path.
          </li>
          <li>
            Confirm that expired or fulfilled orders cannot be edited. Check the
            result and any pending billing in Order Editing activity.
          </li>
        </ol>
      </details>
    </section>
  );
}

export function Preview({ settings }: { settings: OrderEditingSettings }) {
  const { appearance } = normalizeOrderEditingSettings(settings);
  const [previewLanguage, setPreviewLanguage] = useState<'en' | 'es' | 'fr'>(
    'en',
  );
  const [surface, setSurface] = useState('thankYou');
  const locale =
    appearance.locale === 'auto'
      ? previewLanguage
      : appearance.locale;
  const defaults = APPEARANCE_DEFAULTS[locale];
  const copy = (key: keyof typeof defaults) =>
    appearance[key]?.trim() || defaults[key];
  const variant = appearance.buttonVariant;
  const critical = appearance.buttonTone === 'critical';
  const buttonClass =
    variant === 'primary'
      ? `${
          critical ? 'bg-red-700' : 'bg-gray-900'
        } border-transparent text-white`
      : variant === 'secondary'
      ? `${
          critical
            ? 'border-red-700 text-red-700'
            : 'border-gray-400 text-gray-900'
        } bg-white`
      : `${
          critical ? 'text-red-700' : 'text-blue-700'
        } border-transparent underline`;
  return (
    <section
      aria-label="Customer preview"
      className="rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <h2 className="text-base font-semibold text-gray-900">
        Live customer preview
      </h2>
      <p className="mt-1 text-xs text-gray-500">
        Updates as you edit your customer wording and button styles.
      </p>
      <div className="my-4 grid grid-cols-2 gap-3">
        <label className="text-xs font-medium text-gray-700">
          Preview page
          <select
            className={inputClass}
            value={surface}
            onChange={(event) => setSurface(event.target.value)}
          >
            <option value="thankYou">Thank You</option>
            <option value="orderStatus">Order Status</option>
          </select>
        </label>
        <label className="text-xs font-medium text-gray-700">
          Preview language
          <select
            className={inputClass}
            value={locale}
            disabled={appearance.locale !== 'auto'}
            onChange={(event) =>
              setPreviewLanguage(event.target.value as 'en' | 'es' | 'fr')
            }
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
          </select>
        </label>
      </div>
      <p className="rounded-t-md border border-b-0 border-gray-200 bg-gray-100 px-4 py-2 text-xs text-gray-600">
        {surface === 'thankYou' ? 'Thank You' : 'Order Status'} page · Sample
        order #1001
      </p>
      <div
        className="rounded-b-md border border-gray-200 bg-white p-4"
        lang={locale}
      >
        <h3 className="text-lg font-semibold">{copy('title')}</h3>
        <p className="mt-2 text-sm text-gray-600">{copy('subtitle')}</p>
        {settings.editShipping && (
          <div className="mt-5 border-t border-gray-200 pt-4">
            <h4 className="font-medium">{copy('editTitle')}</h4>
            <div className="my-3 rounded border border-gray-200 p-3 text-sm text-gray-500">
              123 Example Street
              <br />
              Portland, OR 97201
            </div>
            <button
              type="button"
              disabled
              className={`rounded border px-3 py-2 text-sm ${buttonClass}`}
            >
              {copy('saveLabel')}
            </button>
          </div>
        )}
        {settings.cancelOrder && (
          <div className="mt-5 border-t border-gray-200 pt-4">
            <h4 className="font-medium">{copy('cancelTitle')}</h4>
            <p className="my-3 text-sm text-gray-600">
              {copy('cancelDescription')}
            </p>
            <button
              type="button"
              disabled
              className={`rounded border px-3 py-2 text-sm ${buttonClass}`}
            >
              {copy('cancelLabel')}
            </button>
            <div className="mt-3 flex gap-3 text-sm">
              <span>{copy('backLabel')}</span>
              <span>{copy('confirmLabel')}</span>
            </div>
          </div>
        )}
        {!settings.editShipping && !settings.cancelOrder && (
          <p className="mt-4 text-sm text-gray-500">
            Both actions are turned off.
          </p>
        )}
      </div>
      <p className="mt-3 text-xs leading-5 text-gray-500">
        Sample content. Shopify applies your checkout theme.{' '}
        {appearance.locale === 'auto'
          ? 'Automatic language follows the customer’s language. Use the preview language to check translations.'
          : 'This preview uses your configured customer language.'}{' '}
        Leave wording empty to use translations. Sample buttons do not change an
        order.
      </p>
    </section>
  );
}
